// Roles y permisos (D99). Los permisos de cada rol van en el objeto permisoRol (una fila por rol de Twenty); los
// roles mismos son los de Twenty y se crean o borran por /metadata (solo un administrador).
import type { FilaPermisos } from '../lib/permisos';
import { metadataSesion } from './http';
import { create, listAll, remove, update } from './rest';

type RawFila = { id: string; name: string | null; rolId: string | null; permisos: string[] | null };

export async function filasPermisos(): Promise<FilaPermisos[]> {
  const items = await listAll<RawFila>('permisosRol', {}, 200);
  return items.filter((f) => f.rolId).map((f) => ({ id: f.id, rolId: f.rolId!, name: f.name ?? '', permisos: f.permisos ?? [] }));
}

/** Guarda los permisos de un rol: actualiza su fila o la crea. */
export async function guardarPermisosRol(rol: { id: string; label: string }, permisos: string[], filaId?: string) {
  const cuerpo = { name: rol.label, rolId: rol.id, permisos: [...permisos].sort() };
  return filaId ? update('permisosRol', filaId, cuerpo) : create('permisosRol', cuerpo);
}

export const borrarFilaPermisos = (id: string) => remove('permisosRol', id);

/* ---------------- roles de Twenty ---------------- */

export type RolCompleto = { id: string; label: string; descripcion: string; icono: string; personas: number; asignable: boolean };

const q = <T>(query: string, variables?: Record<string, unknown>) => metadataSesion<T>(query, variables);

const DATOS_COMO_SUPERVISOR = {
  canUpdateAllSettings: false,
  canAccessAllTools: false,
  canReadAllObjectRecords: true,
  canUpdateAllObjectRecords: true,
  canSoftDeleteAllObjectRecords: true,
  canDestroyAllObjectRecords: false,
  canBeAssignedToUsers: true,
  canBeAssignedToAgents: false,
  canBeAssignedToApiKeys: false,
};

// Roles de fabrica de D99 que la empresa debe tener (Admin y Member ya vienen con Twenty).
const FIJOS: { label: string; description: string; icon: string }[] = [
  { label: 'Supervisor', description: 'Equipo, aprobación de horas, reportes y supervisión de llamadas.', icon: 'IconShield' },
  { label: 'Gerencia', description: 'Ve todo y todos los reportes. No configura ni borra.', icon: 'IconBriefcase' },
  { label: 'Ventas', description: 'Oportunidades, proyectos, bolsas, licencias 3CX y campañas.', icon: 'IconChartLine' },
];

/** Crea los roles de fabrica que falten. Devuelve true si creo alguno. */
export async function asegurarRoles(roles: { label: string }[]) {
  let creo = false;
  for (const r of FIJOS) {
    if (roles.some((x) => x.label === r.label)) continue;
    await crearRol(r.label, r.icon, r.description);
    creo = true;
  }
  return creo;
}

export async function rolesTwenty(): Promise<RolCompleto[]> {
  const consulta = '{ getRoles { id label description icon canBeAssignedToUsers workspaceMembers { id } } }';
  type R = { id: string; label: string; description: string | null; icon: string | null; canBeAssignedToUsers: boolean; workspaceMembers: { id: string }[] };
  let { getRoles } = await q<{ getRoles: R[] }>(consulta);
  if (await asegurarRoles(getRoles).catch(() => false)) ({ getRoles } = await q<{ getRoles: R[] }>(consulta));
  return getRoles
    .filter((r) => r.canBeAssignedToUsers && !/function role/i.test(r.label))
    .map((r) => ({ id: r.id, label: r.label, descripcion: r.description ?? '', icono: r.icon ?? '', personas: r.workspaceMembers.length, asignable: r.canBeAssignedToUsers }));
}

export async function crearRol(label: string, icon: string, description = '') {
  const { createOneRole } = await q<{ createOneRole: { id: string } }>('mutation($i:CreateRoleInput!){ createOneRole(createRoleInput:$i){ id } }', {
    i: { label, description, icon, ...DATOS_COMO_SUPERVISOR },
  });
  return createOneRole.id;
}

export const borrarRol = (id: string) => q('mutation($r:UUID!){ deleteOneRole(roleId:$r) }', { r: id });
